// src/utils/continueJobUtils.js
// ===========================================================================
// "Continue" a job WITHOUT completing it.
//
// The Home page's Continue button does two things: it completes the current
// job and it creates a copy of that job on a new date under a stemmed work
// order (5456 -> 5456-2 -> 5456-3). On the invoice pages the original job is
// finished and often already invoiced or paid, so completing it again makes no
// sense. Everything here does the SECOND half only: a new Active job that
// stems off the same work order number, with the original job left exactly as
// it is — no status change, no completed date, no write of any kind.
//
// The copy is deliberately stripped of anything that belongs to the original
// run of the job: its notes, receipts, completed dates, totals and every
// invoice/paid flag. Carrying those over would make a brand new job show up as
// already invoiced.
// ===========================================================================

import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../firebaseConfig";
import { getMaterialsGroupId } from "./materialsUtils";
import { rollSubPayTermsOnto } from "./subPayPayments";

/**
 * "5456-3" -> "5456". "5456" -> "5456".
 * "PJMC-10025" -> "PJMC-10025". "PJMC-10025-2" -> "PJMC-10025".
 *
 * A trailing "-N" is only a branch number when what is left in front of it
 * still has a digit in it. Proposal work orders are PREFIX-NUMBER, and the old
 * rule stripped the number itself ("PJMC-10025" -> "PJMC"), so continuing a
 * proposal job handed out the next proposal number instead of "-2".
 */
export const getWOStem = (wo) => {
  const value = String(wo ?? "").trim();
  const match = value.match(/^(.*)-(\d+)$/);
  if (!match) return value;
  return /\d/.test(match[1]) ? match[1] : value;
};

const todayKey = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

const dateKey = (value) => {
  if (!value) return "";
  if (typeof value === "object" && value?.seconds) {
    return new Date(value.seconds * 1000).toISOString().slice(0, 10);
  }
  if (typeof value === "string") return value.slice(0, 10);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
};

/*
  Fields that must NOT ride along on the new job.

  Anything that records what happened on the ORIGINAL job: its history, its
  money, and its place in the invoice pipeline. Keeping the shape of the work
  (site, scope, description, tech, property, proposal link) is the whole point
  of a continuation, so those stay.
*/
const FIELDS_NOT_COPIED = [
  // record-keeping, not job data
  "id",
  "__collection",
  "__renderDate",
  "occurrenceDate",
  "newFiles",
  "creationDate",
  "updatedAt",
  // the original run's history
  "notes",
  "receipts",
  "receiptsTotal",
  "completedDates",
  "completedAt",
  "completedBy",
  "occurrenceNotes",
  // money already counted against the original
  "estimatedTotal",
  "jobTotal",
  "total",
  "depositPrice",
  "depositSent",
  // invoice pipeline flags - a new job has not been invoiced or paid
  "approvedInvoice",
  "approvedInvoiceId",
  "approvedInvoiceAt",
  "invoiceSent",
  "invoiceSentDate",
  "invoicedDates",
  "invoiceNumber",
  "invoiceId",
  "invoiceSentFromApprovedInvoiceId",
  "lastInvoiceSentAt",
  "invoiceSentReversedAt",
  "paid",
  "paidAt",
  "paidAmount",
  // links written on a job when IT was continued
  "continuedToDate",
  "continuedToWO",
  "projectContinuedToDate",
  "projectContinuedToWO",
  "projectContinuedToJobId",
  // billing-flow marks that belong to the original run. contractBilling is
  // NOT here on purpose: it is what carries the contract total, deposit and
  // running balance onto the next WO.
  "billingKind",
  "billingDeferred",
  "billingDeferredAt",
  "billingSkipAuto",
  "noPayment",
  "noPaymentReason",
  "noPaymentAt",
  // "Bill Later" belongs to the WO it was picked on; the next WO is the one
  // that gets billed, so it must not arrive already marked.
  "billLater",
  "billLaterAt",
  "billLaterToWO",
  "billLaterToJobId",
  "billLaterFromJobIds",
  "createdFromDepositInvoiceId",
  "invoiceSentAt",
];

/**
 * A copy of `job` with everything that belongs to the original run removed
 * (FIELDS_NOT_COPIED). Used by every Continue - this file's and Home's - so a
 * continued WO never arrives already invoiced or marked paid.
 *
 * keepTotals: Home's Continue has always carried the job total over to the
 * next WO and still does. The billing popup and the automatic sweep check the
 * WO chain, so that total is not billed a second time by mistake.
 */
const TOTAL_FIELDS = ["estimatedTotal", "jobTotal", "total", "depositPrice", "depositSent"];

export const stripForContinuation = (job = {}, { keepTotals = false } = {}) => {
  const copy = { ...job };
  FIELDS_NOT_COPIED.forEach((key) => {
    if (keepTotals && TOTAL_FIELDS.includes(key)) return;
    delete copy[key];
  });
  return copy;
};

/**
 * Next free work order under this stem: 5456 -> 5456-2, 5456-2 -> 5456-3.
 *
 * The number comes from the HIGHEST suffix already in use, not from how many
 * related jobs exist. Counting breaks after a branch is deleted — three jobs
 * numbered 5456, 5456-2, 5456-4 would hand out 5456-4 a second time.
 *
 * Work orders are compared WITHOUT regard to upper/lower case: "pjmc-3041-2"
 * and "PJMC-3041-2" are the same work order. Firestore range queries are
 * case-sensitive, so the stem is scanned in each common spelling (as typed,
 * UPPER, lower, Title) and the results merged.
 */
export const getNextContinuedWO = async (baseWO) => {
  const stem = getWOStem(baseWO);
  if (!stem) return "";

  const lower = stem.toLowerCase();
  const variants = Array.from(
    new Set([stem, stem.toUpperCase(), lower, lower.charAt(0).toUpperCase() + lower.slice(1)])
  );

  // Branch numbers always contain a dash, so they are always strings and
  // always land inside these ranges.
  const snaps = await Promise.all(
    variants.map((v) =>
      getDocs(query(collection(db, "jobs"), where("wo", ">=", v), where("wo", "<=", `${v}\uf8ff`)))
    )
  );

  const stemKey = stem.toUpperCase();
  const taken = new Set();
  let highest = 1;

  snaps.forEach((snap) =>
    snap.forEach((d) => {
      const wo = String(d.data()?.wo ?? "").trim().toUpperCase();
      if (!wo) return;
      taken.add(wo);
      const match = wo.match(/^(.*)-(\d+)$/);
      if (match && match[1] === stemKey) {
        highest = Math.max(highest, Number(match[2]) || 0);
      }
    })
  );

  let next = highest + 1;
  while (taken.has(`${stemKey}-${next}`) && next < highest + 100) next += 1;

  return `${stem}-${next}`;
};

/** The job behind an invoice row: by id first, then by work order. */
export const findJobForWO = async ({ jobId, originalJobId, wo }) => {
  const byId = jobId || originalJobId;

  if (byId) {
    const snap = await getDoc(doc(db, "jobs", byId));
    if (snap.exists()) return { id: snap.id, ...snap.data() };
  }

  const woValue = String(wo ?? "").trim();
  if (woValue) {
    const snap = await getDocs(
      query(collection(db, "jobs"), where("wo", "==", woValue))
    );
    if (!snap.empty) {
      return { id: snap.docs[0].id, ...snap.docs[0].data() };
    }
  }

  return null;
};

/**
 * Creates the continuation job. The source job is READ ONLY here — nothing is
 * written back to it.
 *
 * Returns { id, job, wo } for the job that was created.
 */
export const createContinuationJob = async ({
  sourceJob,
  newDate,
  newWO,
  createdBy,
}) => {
  if (!sourceJob?.id) throw new Error("No source job to continue from.");

  const startDate = dateKey(newDate) || todayKey();
  const wo = newWO || (await getNextContinuedWO(sourceJob.wo));
  const fromDate = dateKey(sourceJob.startDate);

  const user = createdBy || (() => {
    const current = getAuth()?.currentUser;
    const fullName = current?.displayName || "";
    return {
      uid: current?.uid || "",
      firstName: fullName.split(" ")[0] || "",
      lastName: fullName.split(" ").slice(1).join(" ") || "",
      fullName: fullName || current?.email || "User",
    };
  })();

  const copiedJob = stripForContinuation(sourceJob);

  // Materials are shared per group, so the copy points at the same group the
  // original used rather than starting empty.
  copiedJob.materialsGroupId = getMaterialsGroupId(sourceJob);

  if (copiedJob.jobPlan) {
    copiedJob.jobPlan = { ...copiedJob.jobPlan };
    delete copiedJob.jobPlan.materials;
  }

  const newJob = {
    ...copiedJob,

    wo: wo || sourceJob.wo || "",
    startDate,
    endDate: startDate,
    status: "Active",
    recurrence: { type: "none" },

    notes: [],
    receipts: [],
    receiptsTotal: 0,
    completedDates: [],
    occurrenceNotes: {},

    // where it came from. Record only - the two jobs are independent.
    continuedFromJobId: sourceJob.id,
    continuedFromWO: sourceJob.wo || "",
    continuedFromDate: fromDate || "",
    originalStartDate: fromDate || sourceJob.originalStartDate || "",

    createdByContinue: true,
    // Set apart from Home's Continue: that one closes the job it came from,
    // this one leaves it alone.
    continuedWithoutCompleting: true,

    creationDate: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: user,
  };

  Object.keys(newJob).forEach((key) => {
    if (newJob[key] === undefined) delete newJob[key];
  });

  // Sub contractor daily / project pay rolls over (a project price is owed
  // once, on the first job). Lines of subs without pay terms are unchanged.
  Object.assign(newJob, await rollSubPayTermsOnto(newJob));

  const ref = await addDoc(collection(db, "jobs"), newJob);

  return {
    id: ref.id,
    wo: newJob.wo,
    job: { ...newJob, id: ref.id, creationDate: new Date(), updatedAt: new Date() },
  };
};

/*
  Links and marks that say where a job CAME FROM. A duplicate came from the
  job it was copied from, not from that job's parent. If it kept these, it
  would look like a Continue of the parent: Bill Later, sub pay clean-up and
  Jobs Linked follow continuedFromJobId.
*/
const DUPLICATE_DROP_FIELDS = [
  "continuedFromJobId",
  "continuedFromWO",
  "continuedFromDate",
  "originalStartDate",
  "createdByContinue",
  "continuedWithoutCompleting",
  "previousJobId",
  "projectRootId",
  "projectDayNumber",
  "projectContinuedFromJobId",
  "projectContinuedFromDate",
  "createdByProjectContinue",
  "projectCompletedAt",
  "projectCompletedBy",
  "duplicatedFromJobId",
  "duplicatedFromWO",
  "createdByDuplicate",
  // Its own job, so its own contract: the original's contract total is not
  // carried over (no chance of billing it twice). The billing popup asks for a
  // total when the duplicate is completed.
  "contractBilling",
  // Its own materials list, not the original's.
  "materialsGroupId",
];

/**
 * Duplicate a job: a new ACTIVE job under the next free work order
 * (5456 -> 5456-2, same numbering Continue uses). The original is not touched
 * at all - it stays Active, nothing is completed or written back.
 *
 * Copied: site, scope, description, tech assign, property, files, location
 * fence, sub pay terms (rolled the same way Continue rolls them).
 * Not copied: notes, receipts, completed dates, totals, every invoice / paid
 * flag, Continue / project links, contract billing, materials list.
 *
 *   startDate  the duplicate's start date (defaults to the original's). A
 *              multi-day job keeps its length.
 *
 * Returns { id, wo, job }.
 */
export const createDuplicateJob = async ({ sourceJob, startDate, createdBy }) => {
  if (!sourceJob?.id) throw new Error("No job to duplicate.");

  const srcStart = dateKey(sourceJob.startDate);
  const srcEnd = dateKey(sourceJob.endDate) || srcStart;
  const start = dateKey(startDate) || srcStart || todayKey();
  let end = start;
  if (srcStart && srcEnd && srcEnd > srcStart) {
    const spanDays = Math.round(
      (new Date(`${srcEnd}T12:00:00`) - new Date(`${srcStart}T12:00:00`)) / 86400000
    );
    const e = new Date(`${start}T12:00:00`);
    e.setDate(e.getDate() + spanDays);
    end = `${e.getFullYear()}-${String(e.getMonth() + 1).padStart(2, "0")}-${String(e.getDate()).padStart(2, "0")}`;
  }

  const wo = await getNextContinuedWO(sourceJob.wo);

  const user = createdBy || (() => {
    const current = getAuth()?.currentUser;
    const fullName = current?.displayName || "";
    return {
      uid: current?.uid || "",
      firstName: fullName.split(" ")[0] || "",
      lastName: fullName.split(" ").slice(1).join(" ") || "",
      fullName: fullName || current?.email || "User",
    };
  })();

  const copy = stripForContinuation(sourceJob);
  DUPLICATE_DROP_FIELDS.forEach((key) => delete copy[key]);

  if (copy.jobPlan) {
    copy.jobPlan = { ...copy.jobPlan };
    delete copy.jobPlan.materials;
  }
  if (copy.jobFence) copy.jobFence = { ...copy.jobFence, carriedFromJobId: sourceJob.id };

  const newJob = {
    ...copy,

    wo: wo || sourceJob.wo || "",
    startDate: start,
    endDate: end,
    status: "Active",
    recurrence: { type: "none" },

    notes: [],
    receipts: [],
    receiptsTotal: 0,
    completedDates: [],
    occurrenceNotes: {},

    // where it came from. Record only - the two jobs are independent.
    duplicatedFromJobId: sourceJob.id,
    duplicatedFromWO: sourceJob.wo || "",
    createdByDuplicate: true,

    creationDate: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: user,
  };

  Object.keys(newJob).forEach((key) => {
    if (newJob[key] === undefined) delete newJob[key];
  });

  // Sub pay terms: same roll-over as Continue (daily rate carries, a project
  // price stays owed on the original only).
  Object.assign(newJob, await rollSubPayTermsOnto(newJob));

  const ref = await addDoc(collection(db, "jobs"), newJob);

  return {
    id: ref.id,
    wo: newJob.wo,
    job: { ...newJob, id: ref.id, creationDate: new Date(), updatedAt: new Date() },
  };
};
